import React, { useMemo } from 'react';
import { STAFF_PATHS } from '../../../../_configuration';
import { Link } from 'react-router-dom';
import { generatePath, useHistory } from 'react-router';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

// Material UI
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import Divider from '@material-ui/core/Divider';

// Data
import { SPORT_TERMINOLOGY } from '../../../lib/constants';
import { toListSentence } from '../../../lib/helpers/conversion.helpers';
import { Bold } from '../../_core/_ui/typography.component';
import { IntlMsg } from '../../_core/IntlMsg/intl-msg.component';
import { PrimaryButton } from '../../_core/_ui/buttons.component';
import ApproveGroupingsGraphQL from '../ApproveGroupings/approve-groupings.graphql';
import { Sport, SportTerminology } from '../../../lib/types';
import { pluralTerm } from '../../../lib/helpers/term.helper';
import { useCompanyHasAutomation, useCompanySportTerminology } from '../../../lib/stores/company.store';
import { getGroupStageValidationMessage } from '../assessment-stage.constants';
import { DisqualifiedSessionsList } from './disqualified-sessions-list.component';
import { UnassignedPlayersMessage } from './unassigned-players-message.component';
import { messages } from './group-stage-handler.messages';

dayjs.extend(utc)
dayjs.extend(timezone)

interface IGroupStageHandler {
  stage: IAssessmentStage;
  ageGroup: IAgeGroup;
  isNext?: boolean;
  positionHash: { [key: string]: string };
  isSummary?: boolean;
}

export const GroupStageHandler: React.FC<IGroupStageHandler> = ({
  ageGroup,
  stage,
  isNext,
  positionHash,
  isSummary
}) => {
  const hasAutomation = useCompanyHasAutomation()
  const termLookup = useCompanySportTerminology();
  const athleteTerm = termLookup[SportTerminology.ATHLETE];
  const athletesTerm = pluralTerm(athleteTerm);
  const sessionTerm = termLookup[SportTerminology.SESSION];

  const history = useHistory();

  let isInvalid = false;
  const content = [];

  const playerLimitsValidationMessage = useMemo(() => getGroupStageValidationMessage(stage.config.playerLimits), [stage.config.playerLimits]);

  if (isNext) {
    if (stage.order !== 1) {
      content.push(<Box mt={2}><Typography key="first" variant="subtitle1"><Bold>Next Stage:</Bold> Group {athletesTerm}</Typography></Box>);
    }

    return <>{content}</>;
  }

  content.push(
    <Typography key="title" variant="subtitle1"><Bold>Current Stage:</Bold> Group {athletesTerm}</Typography>,
  );

  content.push(<Box my={1} key="divider"><Divider /></Box>);
  if (!ageGroup.numPlayers) {
    isInvalid = true;
    content.push(<Typography key="players">Missing {athletesTerm.toLowerCase()}. <Link to={"/" + pluralTerm(SPORT_TERMINOLOGY[Sport.default][SportTerminology.ATHLETE].toLowerCase())}>Add some here</Link></Typography>);
  }

  if (!ageGroup.numSessions) {
    isInvalid = true;
    content.push(<Typography key="sessions">Missing {pluralTerm(sessionTerm).toLowerCase()}. <Link to="/sessions">Add some here</Link></Typography>);
  }

  if (playerLimitsValidationMessage) {
    isInvalid = true;
    content.push(<Typography key="player-limits">{playerLimitsValidationMessage} <Link to="/format">Set up player limits on the format page</Link></Typography>);
  }

  if (!isInvalid && stage.isReady?.noSessions && hasAutomation) {
    isInvalid = true;
    content.push(
      <Typography key="no-configs">
        No {sessionTerm.toLowerCase()} format found following this grouping. <Link to="/format">Add some here</Link>
      </Typography>
    );
  }

  // Players outside every position limit arrive as ungroupedPlayers instead, so this is only "all concluded or held".
  if (!isInvalid && stage.isReady?.noAthletes) {
    isInvalid = true;
    content.push(
      <Typography key="no-eligible-athletes">
        <IntlMsg
          {...messages.noAthletes}
          values={{
            athletes: athletesTerm.toLowerCase(),
            reviewLink: (
              <Link to={"/" + pluralTerm(SPORT_TERMINOLOGY[Sport.default][SportTerminology.ATHLETE].toLowerCase())}>
                <IntlMsg {...messages.reviewLink} />
              </Link>
            )
          }}
        />
      </Typography>
    );
  }

  if (!isInvalid && (stage.isReady?.ungroupedPlayers || stage.isReady?.shortTrackRuns?.length)) {
    isInvalid = true;
    content.push(
      <UnassignedPlayersMessage
        key="unassigned-players"
        ungroupedPlayers={stage.isReady.ungroupedPlayers}
        shortTrackRuns={stage.isReady.shortTrackRuns}
        recommendedPlayerLimits={stage.isReady.recommendedPlayerLimits}
        playerLimits={stage.config.playerLimits || []}
        positionHash={positionHash}
      />
    );
  }

  if (!isInvalid && typeof stage.isReady?.numFound === 'number' && stage.isReady.numFound < stage.isReady.numRequired) {
    const playerLimits: {positionNames: string[]; limit: number}[] = stage.config.playerLimits.map((playerLimit: IPlayerLimit) => {
      const positionNames = playerLimit.positions.map(positionId => positionHash[positionId]);
      return {positionNames, limit: playerLimit.limit};
    });

    const playerLimitsSentences = playerLimits.map(({positionNames, limit}) => `${toListSentence(positionNames)} to ${limit}`);

    const { athleteCounts, groupsByLimit, numFound, numRequired } = stage.isReady;
    isInvalid = true;

    const insufficientLimits = playerLimits.reduce((acc: {positionNames: string[]; requiredLimit: number}[], {positionNames}, i) => {
      if (groupsByLimit[i] > numFound) {
        acc.push({positionNames, requiredLimit: Math.ceil(athleteCounts[i] / numFound)})
      }
      return acc;
    }, []);

    const insufficientLimitsStrings = insufficientLimits.map(({positionNames, requiredLimit}, i) => `${positionNames.join(", ")} to ${requiredLimit}`)

    const numToAdd = numRequired - numFound
    const sessionOption = <>You need to <Bold>add {numToAdd} {numToAdd > 1 ? pluralTerm(sessionTerm).toLowerCase() : sessionTerm.toLowerCase()}</Bold> and you can do that <Link to="/sessions">here.</Link></>
    const options = numFound > 0 ? <>You either need to <Bold>A) increase your athletes limits {toListSentence(insufficientLimitsStrings)}</Bold> and you can do that <Link to="/format">here</Link>. OR <Bold>B)</Bold> {sessionOption}</> : sessionOption
    content.push(
      <Typography key="missing-sessions">
        {stage.config.playerLimits.length &&
          <>Based on the {athleteTerm.toLowerCase()} limits per {sessionTerm.toLowerCase()} that are setup within your evaluation format for the next round, which are currently set to: <Bold>{toListSentence(playerLimitsSentences)}</Bold> we've calculated that you need to review and update your settings; {options}</>
        }
      </Typography>
    );
  }

  // Every ready result carries these, so the list renders whether or not the round came up
  // short — the branch above only owns the shortfall copy. It self-suppresses when empty.
  content.push(
    <DisqualifiedSessionsList
      key="disqualified-sessions"
      sessions={stage.isReady?.disqualifiedSessions}
      warnings={stage.isReady?.warningSessions}
    />
  );

  if (!isInvalid) {
    if (stage.config.manualApproval) {
      if (isSummary) {
        content.push(
          <PrimaryButton fullWidth onClick={() => history.push(generatePath(STAFF_PATHS.AGE_GROUP, { ageGroupId: ageGroup._id }))} key="review">Review & Approve
            Groupings</PrimaryButton>
        );
      } else {
        content.push(<ApproveGroupingsGraphQL ageGroupId={ageGroup._id} stage={stage} />);
      }
    } else {
      content.push(<Typography>Automated scheduling is not enabled. Please contact your account manager for assistance</Typography>);
    }
  }

  return <Box mt={2}>{content}</Box>;
};
